import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import ActionCreators from "../../actions";
import { setLyftReportData, setTuroReportData, setOtherReportData } from "../../utils/functions";
import { getCertificateFontDownloads } from "../../utils/certificateFontDownloads";
import "./certificateDownloadButton.css";
import moment from "moment";
import {
  PDFDownloadLink,
  Page,
  Text,
  View,
  Image,
  Document,
  StyleSheet,
  Font,
  Svg,
  Path,
} from "@react-pdf/renderer";

import kalamFont from "../../font/Kalam-Light.ttf";
import "../../font/registerPatrickHandAndCaveatPdf";

Font.register({ family: "kalamFont", format: "truetype", src: kalamFont });

const LYFT_LOGO_URL =
  "https://res.cloudinary.com/dgjordf6e/image/upload/v1742279338/image_22_kh5uqw.png";

const inspectionColumns = [
  [
    { number: 1, label: "Foot brakes", aliases: ["foot brake", "foot brakes", "brake"] },
    {
      number: 2,
      label: "Emergency brake (parking brake)",
      aliases: ["emergency brake", "parking brake"],
    },
    { number: 3, label: "Steering mechanism", aliases: ["steering"] },
    {
      number: 4,
      label: "Windshield",
      aliases: ["windshield"],
      subChecks: [
        { label: "Large crack", aliases: ["large crack"] },
        { label: "Small crack", aliases: ["small crack"] },
      ],
    },
    {
      number: 5,
      label: "Rear window and other glass",
      aliases: ["rear window", "other glass"],
    },
    { number: 6, label: "Windshield wipers", aliases: ["wiper", "wipers"] },
    { number: 7, label: "Headlights", aliases: ["headlight", "headlights"] },
    { number: 8, label: "Tail lights", aliases: ["tail light", "tail lights"] },
    {
      number: 9,
      label: "Turn indicator lights",
      aliases: ["turn indicator", "turn signal", "indicator lights"],
    },
    { number: 10, label: "Stop lights", aliases: ["stop light", "brake light"] },
    {
      number: 11,
      label: "Front seat adjustment",
      aliases: ["front seat adjustment", "seat adjustment"],
    },
    {
      number: 12,
      label: "Doors (open, close, lock)",
      aliases: ["door", "doors"],
    },
  ],
  [
    { number: 13, label: "Horn", aliases: ["horn"] },
    { number: 14, label: "Speedometer", aliases: ["speedometer"] },
    { number: 15, label: "Bumpers", aliases: ["bumper", "bumpers"] },
    {
      number: 16,
      label: "Muffler and exhaust system",
      aliases: ["muffler", "exhaust"],
    },
    {
      number: 17,
      label: "Tires, incl. tread depth",
      aliases: ["tire", "tires", "tread depth"],
      subChecks: [
        { label: "Left front", aliases: ["left front tire"] },
        { label: "Right front", aliases: ["right front tire"] },
        { label: "Left rear", aliases: ["left rear tire"] },
        { label: "Right rear", aliases: ["right rear tire"] },
      ],
    },
    {
      number: 18,
      label: "Interior and exterior rear view mirrors",
      aliases: ["rear view mirror", "rearview mirror", "mirrors"],
    },
    {
      number: 19,
      label: "Safety belts for driver and passenger(s)",
      aliases: ["safety belt", "seat belt"],
    },
  ],
];

const getFullName = (data = {}) =>
  `${data?.Username || ""} ${data?.UserlastName || ""}`.trim();

const getInspectorName = (data = {}) =>
  `${data?.Inspectorname || ""} ${data?.InspectorlastName || ""}`.trim();

const formatDate = (value) =>
  value ? moment(new Date(value)).format("MM/DD/YYYY") : "";

const handDrawnCirclePath =
  "M52.1927 2.90815C34.1317 2.90815 15.3734 8.31886 6.56927 25.4371C3.77005 30.8798 1.5 32.7484 1.5 38.8919C1.5 44.4471 5.2551 50.3534 10.1902 53.2854C14.6663 55.9447 15.5908 61.4646 20.3287 63.7676C25.118 66.0956 26.9011 68.1651 32.5593 69.1652C42.084 70.8488 57.582 73.3293 66.6763 69.3999C70.4649 67.7629 77.2718 68.2962 81.4818 67.366C84.6931 66.6565 103.112 62.3328 104.253 58.4483C106.691 50.1537 115.056 36.3085 107.23 28.2532C102.352 23.2319 97.9017 18.6987 91.2985 15.5807C86.0695 13.1115 82.0045 12.4543 76.8149 10.6525C70.3868 8.42063 63.9661 4.31621 57.262 4.31621C51.9098 4.31621 47.9811 4.76555 43.5025 1.50009";

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#fff",
    color: "#111",
    fontFamily: "Helvetica",
    paddingBottom: 0,
  },
  header: {
    alignItems: "center",
    backgroundColor: "#000",
    color: "#fff",
    flexDirection: "row",
    height: 48,
    justifyContent: "space-between",
    paddingHorizontal: 44,
  },
  brand: {
    alignItems: "center",
    flexDirection: "row",
  },
  logo: {
    height: 22,
    objectFit: "contain",
    width: 52,
  },
  divider: {
    backgroundColor: "#fff",
    height: 26,
    marginHorizontal: 16,
    width: 1,
  },
  stateText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: 700,
  },
  headerTitle: {
    color: "#fff",
    fontSize: 16,
    fontWeight: 700,
  },
  driverSection: {
    paddingHorizontal: 44,
    paddingTop: 8,
  },
  titleRow: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
  },
  driverTitle: {
    fontSize: 16,
    fontWeight: 800,
  },
  mandatory: {
    fontSize: 8.5,
    fontWeight: 800,
  },
  row: {
    flexDirection: "row",
    marginBottom: 4,
  },
  field: {
    marginRight: 14,
  },
  fieldValue: {
    borderBottomColor: "#777",
    borderBottomWidth: 1,
    fontSize: 8.5,
    minHeight: 13,
    paddingBottom: 1,
  },
  fieldLabel: {
    fontSize: 7.4,
    marginTop: 2,
    textTransform: "uppercase",
  },
  wHalf: {
    flex: 1,
  },
  wPlate: {
    width: 210,
  },
  wVin: {
    flex: 1,
  },
  wMake: {
    width: 210,
  },
  wModel: {
    flex: 1,
  },
  wYear: {
    width: 70,
  },
  wSignature: {
    width: 210,
  },
  noMargin: {
    marginRight: 0,
  },
  vinBoxes: {
    borderBottomColor: "#777",
    borderBottomWidth: 1,
    flexDirection: "row",
    height: 15,
  },
  vinBox: {
    alignItems: "center",
    borderLeftColor: "#777",
    borderLeftWidth: 1,
    flex: 1,
    justifyContent: "center",
  },
  vinChar: {
    fontSize: 8.5,
  },
  inspectorLine: {
    borderBottomColor: "#777",
    borderBottomStyle: "dashed",
    borderBottomWidth: 1.5,
    marginTop: 1,
    position: "relative",
  },
  inspectorLineText: {
    alignSelf: "center",
    backgroundColor: "#fff",
    fontSize: 15,
    fontWeight: 800,
    marginBottom: -9,
    paddingHorizontal: 8,
  },
  checklist: {
    paddingHorizontal: 44,
    paddingTop: 14,
  },
  checklistTitle: {
    fontSize: 16,
    fontWeight: 800,
    marginBottom: 9,
  },
  checklistGrid: {
    flexDirection: "row",
  },
  checkColumn: {
    flex: 1,
  },
  leftColumn: {
    marginRight: 34,
  },
  checkHeading: {
    alignItems: "center",
    color: "#777",
    flexDirection: "row",
    fontSize: 9.5,
    marginBottom: 4,
    textTransform: "uppercase",
  },
  pointHeading: {
    flex: 1,
  },
  statusHeading: {
    textAlign: "center",
    width: 31,
  },
  checkRow: {
    alignItems: "center",
    flexDirection: "row",
    minHeight: 15.5,
  },
  checkLabel: {
    flex: 1,
    flexDirection: "row",
  },
  checkNumber: {
    color: "#777",
    fontSize: 9.5,
    textAlign: "right",
    width: 22,
  },
  checkText: {
    fontSize: 9.6,
    fontWeight: 800,
    lineHeight: 1.15,
  },
  subCheckText: {
    fontSize: 9.6,
    marginLeft: 34,
  },
  boxWrap: {
    alignItems: "center",
    width: 31,
  },
  bodyBoxWrap: {
    width: 62,
  },
  bodyReinspectBoxWrap: {
    width: 84,
  },
  box: {
    borderColor: "#777",
    borderWidth: 1,
    height: 10,
    position: "relative",
    width: 10,
  },
  tick: {
    height: 14,
    left: -2,
    position: "absolute",
    top: -5,
    width: 14,
  },
  checkValue: {
    flexDirection: "row",
    fontSize: 12.5,
    minHeight: 21,
    paddingLeft: 25,
  },
  checkValueLine: {
    borderBottomColor: "#777",
    borderBottomWidth: 1.3,
    marginLeft: 8,
    minHeight: 15,
    textAlign: "center",
    width: 70,
  },
  bodyFull: {
    marginTop: 18,
  },
  bodyRow: {
    flexDirection: "row",
    marginTop: 8,
  },
  bodyMain: {
    flex: 1.05,
    marginRight: 30,
  },
  bodySide: {
    flex: 0.95,
    paddingTop: 30,
  },
  bodyHeading: {
    alignItems: "center",
    color: "#555",
    flexDirection: "row",
    fontSize: 12.5,
    marginBottom: 5,
    textTransform: "uppercase",
  },
  bodyPointHeading: {
    flex: 1,
  },
  bodyStatusHeading: {
    textAlign: "center",
    width: 62,
  },
  bodyReinspectHeading: {
    width: 84,
  },
  bodyCheck: {
    alignItems: "center",
    flexDirection: "row",
  },
  bodyText: {
    flex: 1,
    flexDirection: "row",
    fontSize: 12.2,
  },
  carMap: {
    height: 150,
    marginLeft: -22,
    objectFit: "contain",
    width: 440,
  },
  resultBox: {
    borderColor: "#777",
    borderStyle: "dashed",
    borderWidth: 1,
    alignSelf: "flex-end",
    marginTop: 10,
    minHeight: 48,
    paddingHorizontal: 14,
    paddingTop: 12,
    width: 270,
  },
  resultTop: {
    alignItems: "center",
    flexDirection: "row",
  },
  resultTitle: {
    flex: 1,
    fontSize: 9.8,
    fontWeight: 800,
  },
  resultCircleOne: {
    color: "#666",
    fontSize: 8.8,
    fontWeight: 400,
  },
  resultOption: {
    alignItems: "center",
    height: 28,
    justifyContent: "center",
    fontSize: 9.8,
    fontWeight: 800,
    position: "relative",
    textAlign: "center",
    width: 46,
  },
  resultOptionText: {
    fontSize: 9.8,
    fontWeight: 800,
    textAlign: "center",
  },
  resultCircle: {
    height: 30,
    left: 7,
    position: "absolute",
    top: -1,
    width: 42,
  },
  resultNote: {
    color: "#666",
    fontSize: 11.5,
    lineHeight: 1.2,
    marginTop: 6,
    textAlign: "center",
  },
  tnc: {
    alignSelf: "flex-end",
    borderColor: "#777",
    borderStyle: "dashed",
    borderWidth: 1.5,
    marginTop: 8,
    padding: 6,
    width: 190,
  },
  tncText: {
    borderBottomColor: "#777",
    borderBottomWidth: 1.5,
    fontSize: 13.5,
    fontWeight: 800,
  },
  tncSub: {
    fontSize: 9.5,
    fontWeight: 700,
    marginTop: 4,
  },
  inspectorSection: {
    backgroundColor: "#e8e3e3",
    marginHorizontal: 34,
    marginTop: 10,
    paddingHorizontal: 14,
    paddingBottom: 8,
    paddingTop: 10,
  },
  inspectorTitle: {
    fontSize: 10.5,
    fontWeight: 800,
    marginBottom: 4,
  },
  inspectorNote: {
    fontSize: 8,
    fontWeight: 800,
    lineHeight: 1.2,
    marginBottom: 12,
  },
  certifyRow: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 8,
  },
  certifyBox: {
    borderColor: "#777",
    borderWidth: 1,
    height: 10,
    marginRight: 8,
    width: 10,
  },
  certifyText: {
    flex: 1,
    fontSize: 8.5,
    fontWeight: 800,
  },
  signatureImage: {
    height: 20,
    objectFit: "contain",
    width: 110,
  },
});

const Field = ({ label, value, style, handwritingStyle }) => (
  <View style={[styles.field, style].filter(Boolean)}>
    <Text style={[styles.fieldValue, handwritingStyle].filter(Boolean)}>
      {value || ""}
    </Text>
    <Text style={styles.fieldLabel}>{label}</Text>
  </View>
);

const VinField = ({ value, handwritingStyle }) => {
  const chars = String(value || "").slice(0, 17).split("");
  return (
    <View style={[styles.field, styles.wVin, styles.noMargin].filter(Boolean)}>
      <View style={styles.vinBoxes}>
        {Array.from({ length: 17 }).map((_, index) => (
          <View style={styles.vinBox} key={index}>
            <Text style={[styles.vinChar, handwritingStyle].filter(Boolean)}>
              {chars[index] || ""}
            </Text>
          </View>
        ))}
      </View>
      <Text style={styles.fieldLabel}>VIN</Text>
    </View>
  );
};

const CheckBox = ({ checked, wrapStyle }) => (
  <View style={[styles.boxWrap, wrapStyle].filter(Boolean)}>
    <View style={styles.box}>
      {checked ? (
        <Svg style={styles.tick} viewBox="0 0 24 24">
          <Path
            d="M4 12.5C6.3 15.4 8.1 17.7 9.2 18.7C12.5 13.5 16.1 8.8 21 4.4"
            stroke="#111"
            strokeWidth={2.7}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </Svg>
      ) : null}
    </View>
  </View>
);

const InspectionItem = ({ item, data }) => {
  return (
    <>
      <View style={styles.checkRow}>
        <View style={styles.checkLabel}>
          <Text style={styles.checkNumber}>{item.number}. </Text>
          <Text style={styles.checkText}>{item.label}</Text>
        </View>
        <CheckBox checked />
        <CheckBox checked={false} />
      </View>
      {item.subChecks?.map((subCheck) => {
        return (
          <View style={styles.checkRow} key={subCheck.label}>
            <View style={styles.checkLabel}>
              <Text style={styles.subCheckText}>{subCheck.label}</Text>
            </View>
            <CheckBox checked />
            <CheckBox checked={false} />
          </View>
        );
      })}
      {item.valueLabel ? (
        <View style={styles.checkValue}>
          <Text>{item.valueLabel}</Text>
          <Text style={styles.checkValueLine}>{data?.[item.valueKey] || ""}</Text>
        </View>
      ) : null}
    </>
  );
};

const ResultOption = ({ selected, children }) => (
  <View style={styles.resultOption}>
    {selected ? (
      <Svg style={styles.resultCircle} viewBox="0 0 112 73">
        <Path
          d={handDrawnCirclePath}
          stroke="#000"
          strokeWidth={3}
          strokeLinecap="round"
          fill="none"
        />
      </Svg>
    ) : null}
    <Text style={styles.resultOptionText}>{children}</Text>
  </View>
);

function LyftCertificateNewMexicoDownLoad({
  vehicleChecksArray,
  certificateData = {},
  handwritingFont = "kalamFont",
  fileName = "Vehicle-certificate.pdf",
  linkLabel = "Download pdf",
}) {
  const handwritingStyle = { fontFamily: handwritingFont };
  const finalStatus = String(certificateData?.finalStatus || "").toLowerCase();

  return (
    <div className="App">
      <PDFDownloadLink
        document={
          <Document>
            <Page size="LETTER" style={styles.page} wrap={false}>
              <View style={styles.header} wrap={false}>
                <View style={styles.brand}>
                  <Image style={styles.logo} source={`${LYFT_LOGO_URL}?x-request=xhr`} />
                  <View style={styles.divider} />
                  <Text style={styles.stateText}>NEW MEXICO</Text>
                </View>
                <Text style={styles.headerTitle}>ASE VEHICLE INSPECTION</Text>
              </View>

              <View style={styles.driverSection} wrap={false}>
                <View style={styles.titleRow}>
                  <Text style={styles.driverTitle}>LYFT DRIVER INFO</Text>
                  <Text style={styles.mandatory}>ALL FIELDS ARE MANDATORY</Text>
                </View>
                <View style={styles.row}>
                  <Field
                    label="LYFT DRIVER NAME"
                    value={getFullName(certificateData)}
                    style={styles.wHalf}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="EMAIL ADDRESS"
                    value={certificateData?.Useremail}
                    style={[styles.wHalf, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={styles.row}>
                  <Field
                    label="LICENSE PLATE #"
                    value={certificateData?.licensePlateNumber}
                    style={styles.wPlate}
                    handwritingStyle={handwritingStyle}
                  />
                  <VinField
                    value={certificateData?.vin}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={styles.row}>
                  <Field
                    label="VEHICLE MAKE"
                    value={certificateData?.make}
                    style={styles.wMake}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="VEHICLE MODEL"
                    value={certificateData?.model}
                    style={styles.wModel}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="VEHICLE YEAR"
                    value={certificateData?.year}
                    style={[styles.wYear, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={styles.row}>
                  <Field
                    label="LYFT DRIVER SIGNATURE"
                    style={styles.wSignature}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="LYFT DRIVER PHONE NUMBER"
                    value={certificateData?.Userphone}
                    style={[styles.wHalf, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
              </View>

              <View style={styles.checklist} wrap={false}>
                <Text style={styles.checklistTitle}>
                  ASE VEHICLE INSPECTION CHECKLIST
                </Text>
                <View style={styles.checklistGrid}>
                  {inspectionColumns.map((column, columnIndex) => (
                    <View
                      style={
                        columnIndex === 0
                          ? [styles.checkColumn, styles.leftColumn]
                          : styles.checkColumn
                      }
                      key={columnIndex}
                    >
                      <View style={styles.checkHeading}>
                        <Text style={styles.pointHeading}>INSPECTION POINT</Text>
                        <Text style={styles.statusHeading}>PASS</Text>
                        <Text style={styles.statusHeading}>FAIL</Text>
                      </View>
                      {column.map((item) => (
                        <InspectionItem
                          item={item}
                          checks={vehicleChecksArray}
                          data={certificateData}
                          key={item.number}
                        />
                      ))}
                    </View>
                  ))}
                </View>

                <View style={styles.resultBox}>
                  <View style={styles.resultTop}>
                    <Text style={styles.resultTitle}>
                      VEHICLE INSPECTION{" "}
                      <Text style={styles.resultCircleOne}>(Circle One)</Text>
                    </Text>
                    <ResultOption selected={finalStatus !== "fail"}>
                      PASS
                    </ResultOption>
                    <ResultOption selected={finalStatus === "fail"}>
                      FAIL
                    </ResultOption>
                  </View>
                </View>
              </View>

              <View style={styles.inspectorSection} wrap={false}>
                <Text style={styles.inspectorTitle}>
                  TO BE COMPLETED BY INSPECTOR
                </Text>
                <Text style={styles.inspectorNote}>
                  Note: This inspection shall be performed by an American Advanced
                  Technicians Institute (AATI) certified mechanic or a National
                  Institute for Automotive Service Excellence (ASE) certified mechanic
                  or by a mechanic that is under the supervision of an AATI or ASE
                  certified mechanic.
                </Text>
                <View style={styles.row}>
                  <Field
                    label="COMPANY NAME"
                    value="Chex Inc."
                    style={styles.wHalf}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="INSPECTOR NAME"
                    value={getInspectorName(certificateData)}
                    style={[styles.wHalf, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={styles.row}>
                  <Field
                    label="COMPANY/FACILITY ADDRESS"
                    value={certificateData?.stateAddress}
                    style={styles.wHalf}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="INSPECTION DATE (document expires one year from this date)"
                    value={formatDate(certificateData?.reviewedDate)}
                    style={[styles.wHalf, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={styles.row}>
                  <Field
                    label="VEHICLE MILEAGE"
                    value={certificateData?.milage}
                    style={styles.wHalf}
                    handwritingStyle={handwritingStyle}
                  />
                  <Field
                    label="AATI/ASE CERTIFICATION NO."
                    style={[styles.wHalf, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
                <View style={styles.certifyRow}>
                  <View style={styles.certifyBox} />
                  <Text style={styles.certifyText}>
                    I certify that I am qualified to conduct brake inspections
                    pursuant to the requirements of 49 CFR - Part 396.25.
                  </Text>
                </View>
                <View style={styles.row}>
                  <View style={[styles.field, styles.wHalf].filter(Boolean)}>
                    <View style={styles.fieldValue}>
                      {certificateData?.Inspectorsignature ? (
                        <Image
                          style={styles.signatureImage}
                          source={`${process.env.REACT_APP_AWS_S3_LINK}/${certificateData.Inspectorsignature}?x-request=xhr`}
                        />
                      ) : null}
                    </View>
                    <Text style={styles.fieldLabel}>INSPECTOR SIGNATURE</Text>
                  </View>
                  <Field
                    label="NAME OF SUPERVISING AATI OR ASE CERTIFIED MECHANIC (IF DIFFERENT FROM ABOVE)"
                    style={[styles.wHalf, styles.noMargin].filter(Boolean)}
                    handwritingStyle={handwritingStyle}
                  />
                </View>
              </View>
            </Page>
          </Document>
        }
        fileName={fileName}
      >
        {({ loading }) => (
          <>
            {loading ? (
              <div className="certificate-download-button is-loading" aria-live="polite">
                <span className="certificate-download-spinner" />
                <div>Preparing PDF</div>
              </div>
            ) : (
              <div className="certificate-download-button">
                <div>{linkLabel}</div>
                <i className="fa fa-download" />
              </div>
            )}
          </>
        )}
      </PDFDownloadLink>
    </div>
  );
}


const LyftCertificateNewMexicoDownLoadConnected = (props) => {
  const { id, companyId, reportData, checksData } = props;
  React.useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      props.getVehicleCertificate({ id, companyId }, props.setLoading, "template3");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);
  return (
    <div className="certificate-download-group">
      {!reportData ? (
        <div className="certificate-download-button is-loading" aria-live="polite">
          <span className="certificate-download-spinner" />
          <div>Loading certificate data</div>
        </div>
      ) : (
        getCertificateFontDownloads(reportData?.certificateFont).map((cfg) => (
          <LyftCertificateNewMexicoDownLoad
            key={cfg.handwritingFont}
            reportData={reportData} certificateData={reportData}
            checksData={checksData} checks={checksData} vehicleChecksArray={checksData} firstHalfData={checksData}
            handwritingFont={cfg.handwritingFont}
            fileName={cfg.fileName}
            linkLabel={cfg.linkLabel}
          />
        ))
      )}
    </div>
  );
};

function mapStateToProps(state) {
  const sliceData = state && state.vehicleInstruction && state.vehicleInstruction.lyftVehicleCertificate;
  return {
    reportData: sliceData,
    checksData: setLyftReportData(sliceData && sliceData.Files),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(ActionCreators, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(LyftCertificateNewMexicoDownLoadConnected);
